/**
 * MasonryReflow
 *
 * Creates a true left-to-right masonry layout by taking over from CSS
 * column-count and physically distributing children into flex column wrappers.
 *
 * Why not just reorder DOM children?
 *   CSS column-count balances columns by HEIGHT, not item count. With
 *   variable-height cards, the browser may put 3 items in one column and
 *   1 in another — making any count-based reorder unreliable.
 *
 * How this works instead:
 *   1. Reads the desired column count from Tailwind's `columns-*` classes
 *      (responsive prefixes like md:columns-2 lg:columns-3 are supported)
 *   2. Overrides CSS column-count and switches the container to flexbox
 *   3. Creates column wrapper <div>s and distributes items round-robin
 *      (item 0 → col 0, item 1 → col 1, …, item N → col N % cols)
 *   4. Re-applies on window resize when the breakpoint column count changes
 *
 * Usage:
 *   Add class "masonry-reflow" to any container with Tailwind column classes:
 *   <div class="columns-1 md:columns-2 lg:columns-3 gap-8 masonry-reflow">
 *
 * The existing `gap-*` class provides horizontal spacing between columns.
 * Vertical spacing between cards comes from their own margin (e.g. `mb-8`).
 */

export default class MasonryReflow {
  static SELECTOR = '.masonry-reflow';

  // Tailwind default breakpoints, ordered ascending so the largest
  // matching breakpoint wins (mobile-first cascade).
  static BREAKPOINTS = [
    ['sm', 640],
    ['md', 768],
    ['lg', 1024],
    ['xl', 1280],
    ['2xl', 1536],
  ];

  constructor(container) {
    this.container = container;
    this.items = Array.from(container.children);
    this.currentColumns = 0;
    this.columnConfig = this._parseColumnClasses();

    this.apply();

    this._onResize = this._debounce(() => this.apply(), 75);
    window.addEventListener('resize', this._onResize);
  }

  /**
   * Parse Tailwind `columns-{n}` classes (with optional responsive prefix)
   * into a lookup like { base: 1, md: 2, lg: 3 }.
   */
  _parseColumnClasses() {
    const config = {};

    for (const cls of this.container.classList) {
      const match = cls.match(/^(?:([\w]+):)?columns-(\d+)$/);
      if (match) {
        const bp = match[1] || 'base';
        config[bp] = parseInt(match[2], 10);
      }
    }

    return config;
  }

  /**
   * Determine the current column count by checking window width against
   * the parsed responsive breakpoints (mobile-first: largest matching wins).
   */
  getColumnCount() {
    const width = window.innerWidth;
    let columns = this.columnConfig.base || 1;

    for (const [bp, minWidth] of MasonryReflow.BREAKPOINTS) {
      if (width >= minWidth && this.columnConfig[bp] !== undefined) {
        columns = this.columnConfig[bp];
      }
    }

    return columns;
  }

  /** Rebuild the column layout only when the column count changes. */
  apply() {
    const columns = this.getColumnCount();
    if (columns === this.currentColumns) return;
    this.currentColumns = columns;

    // Detach all current children (items or previous column wrappers)
    while (this.container.firstChild) {
      this.container.removeChild(this.container.firstChild);
    }

    if (columns <= 1) {
      // Single column: restore original CSS-column behaviour, no wrappers
      this.container.style.removeProperty('column-count');
      this.container.style.removeProperty('display');
      this.container.style.removeProperty('align-items');
      const frag = document.createDocumentFragment();
      for (const item of this.items) frag.appendChild(item);
      this.container.appendChild(frag);
      return;
    }

    // Override CSS columns → flex row of column wrappers
    this.container.style.columnCount = 'auto';
    this.container.style.display = 'flex';
    this.container.style.alignItems = 'flex-start';

    // Build column wrapper divs
    const colDivs = [];
    for (let i = 0; i < columns; i++) {
      const col = document.createElement('div');
      col.style.flex = '1';
      col.style.minWidth = '0';
      colDivs.push(col);
    }

    // Round-robin distribution: item 0→col 0, item 1→col 1, …
    for (let i = 0; i < this.items.length; i++) {
      colDivs[i % columns].appendChild(this.items[i]);
    }

    const frag = document.createDocumentFragment();
    for (const col of colDivs) frag.appendChild(col);
    this.container.appendChild(frag);
  }

  /** Tear down: remove listeners, restore original DOM and styles. */
  destroy() {
    window.removeEventListener('resize', this._onResize);

    while (this.container.firstChild) {
      this.container.removeChild(this.container.firstChild);
    }

    this.container.style.removeProperty('column-count');
    this.container.style.removeProperty('display');
    this.container.style.removeProperty('align-items');

    const frag = document.createDocumentFragment();
    for (const item of this.items) frag.appendChild(item);
    this.container.appendChild(frag);
    this.currentColumns = 0;
  }

  _debounce(fn, delay) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  }

  static init(scope = document) {
    const containers = scope.querySelectorAll(MasonryReflow.SELECTOR);
    return Array.from(containers).map((el) => new MasonryReflow(el));
  }
}
